import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useFetch from 'use-http';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Heading } from '@/components/ui/shared/heading';
import { useUserState } from '@/components/context/UserContext';

import { usePatientRegistrationSchema } from './usePatientRegistrationSchema';
import InputDropdown from './InputDropdown';
import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import { useCartData } from '../../context/CartDataContext';
import { useTranslation } from 'react-i18next';
import {
  ChildAgeRestrictionDialog,
  UnderageDialog,
} from './PatientRegistrationDialogs';

// Registration context
import { useRegistration } from '../../context/RegistrationContext';

/* ---------------------------------------------
   Helpers
---------------------------------------------- */

function buildPayerPrefill(userState) {
  if (!userState?.isLoggedIn) return {};
  const u = userState.user ?? userState;
  return {
    payerFirstName: (u?.firstName || '').trim(),
    payerLastName: (u?.lastName || '').trim(),
    payerPersonalCode: (u?.personalCode || '').trim(),
    payerPhone: (u?.phoneNumber ?? u?.phone ?? '').trim(),
    payerEmail: (u?.email || '').trim(),
  };
}

function createApplyPrefill(form, lockedRef, forceRerender) {
  return function applyPrefill(data) {
    let changed = false;
    Object.entries(data).forEach(([name, value]) => {
      if (!value) return;
      const current = form.getValues(name);
      if (!current) {
        form.setValue(name, String(value).trim(), {
          shouldValidate: false,
          shouldDirty: false,
        });
        lockedRef.current.add(name);
        changed = true;
      }
    });
    if (changed) forceRerender((x) => x + 1);
  };
}

function createTrimOnBlur(form) {
  return function trimOnBlur(fieldApi) {
    return (e) => {
      fieldApi.onBlur?.();
      const trimmed = (e.target.value || '').trim();
      if (trimmed !== e.target.value) {
        form.setValue(fieldApi.name, trimmed, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    };
  };
}

function toContactItems(contacts) {
  return (contacts ?? []).map((k) => {
    const name = (k?.name ?? '').trim();
    const surname = (k?.surname ?? '').trim();
    const personalCode = (k?.personalCode ?? '').trim();
    const espbiEsi = (k?.espbiEsi ?? '').trim();
    const display = `${name} ${surname}`.trim();
    return {
      value: display,
      label: display,
      person: { name, surname, personalCode, espbiEsi },
    };
  });
}

function shallowEqualKids(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i],
      y = b[i];
    if (
      (x?.name ?? '') !== (y?.name ?? '') ||
      (x?.surname ?? '') !== (y?.surname ?? '') ||
      (x?.personalCode ?? '') !== (y?.personalCode ?? '')
    )
      return false;
  }
  return true;
}

function deriveKids(contacts, maxAgeFromPersonalCode) {
  const out = [];
  (contacts ?? []).forEach((k) => {
    const pc = k?.personalCode;
    if (pc) {
      const isAdult = maxAgeFromPersonalCode(pc, 18);
      if (!isAdult) out.push(k);
    } else {
      out.push(k);
    }
  });
  return out;
}

/* ---------------------------------------------
   Component
---------------------------------------------- */

const PatientRegistrationForm = ({ onValid, onInvalid }) => {
  const userState = useUserState();
  const schema = usePatientRegistrationSchema();
  const { service } = useCartData();
  const { t } = useTranslation();

  const { userInfo, setUserInfo } = useRegistration();

  const [showUnderageDialog, setShowUnderageDialog] = useState(false);
  const [showChildAgeRestrictionModal, setShowChildAgeRestrictionModal] =
    useState(false);

  const [localChildSelected, setLocalChildSelected] = useState(false);
  const [localSelectedChildEspbiEsi, setLocalSelectedChildEspbiEsi] =
    useState('');

  const [validatePersonalCode, maxAgeFromPersonalCode] = useMemo(
    () => personalCodeValidation(),
    [],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      registerChild: false,
      payerFirstName: '',
      payerLastName: '',
      payerPhone: '',
      payerEmail: '',
      payerPersonalCode: '',
      patientFirstName: '',
      patientLastName: '',
      patientPersonalCode: '',
    },
    mode: 'onSubmit',
    shouldFocusError: true,
  });

  const { watch, reset, setValue } = form;

  // Force child mode if service is children-only
  useEffect(() => {
    if (service?.isChildrenOnly) {
      setValue('registerChild', true, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [service?.isChildrenOnly, setValue]);

  const registerChild = watch('registerChild');

  const lockedRef = useRef(new Set());
  const [, forceRerender] = useState(0);
  const applyPrefill = useMemo(
    () => createApplyPrefill(form, lockedRef, forceRerender),
    [form],
  );
  const trimOnBlur = useMemo(() => createTrimOnBlur(form), [form]);

  const [kids, setKids] = useState([]);
  useEffect(() => {
    const contacts = userState?.contacts ?? [];
    const nextKids = deriveKids(contacts, maxAgeFromPersonalCode);
    setKids((prev) => (shallowEqualKids(prev, nextKids) ? prev : nextKids));
  }, [userState?.contacts, maxAgeFromPersonalCode]);

  const peopleItems = useMemo(() => toContactItems(kids), [kids]);
  const hasSuggestions = !!userState?.isLoggedIn && peopleItems.length > 0;

  useEffect(() => {
    if (!userInfo) return;
    reset({
      registerChild: Boolean(
        userInfo.registerChild ||
          userInfo.patientFirstName ||
          userInfo.patientLastName ||
          userInfo.patientPersonalCode,
      ),
      payerFirstName: userInfo.payerFirstName ?? '',
      payerLastName: userInfo.payerLastName ?? '',
      payerPhone: userInfo.payerPhone ?? '',
      payerEmail: userInfo.payerEmail ?? '',
      payerPersonalCode: userInfo.payerPersonalCode ?? '',
      patientFirstName: userInfo.patientFirstName ?? '',
      patientLastName: userInfo.patientLastName ?? '',
      patientPersonalCode: userInfo.patientPersonalCode ?? '',
    });
    setLocalChildSelected(Boolean(userInfo.childSelected));
    setLocalSelectedChildEspbiEsi(userInfo.selectedChildEspbiEsi || '');
  }, [userInfo, reset]);

  const didPrefillRef = useRef(false);
  useEffect(() => {
    if (!userState?.isLoggedIn) return;
    if (didPrefillRef.current) return;
    const prefill = buildPayerPrefill(userState);
    applyPrefill(prefill);
    didPrefillRef.current = true;
  }, [userState?.isLoggedIn, userState, applyPrefill]);

  const isLocked = (name) => lockedRef.current.has(name);

  const handleSubmit = form.handleSubmit(
    (values) => {
      const merged = {
        ...values,
        childSelected: localChildSelected,
        selectedChildEspbiEsi: localSelectedChildEspbiEsi,
      };
      setUserInfo(merged);
      onValid?.(merged);
    },
    (errors) => {
      // 👇 find first error field
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const el = document.querySelector(`[name="${firstErrorKey}"]`);
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    () => {
      const pc = form.getValues('payerPersonalCode');
      const isUnder18 =
        pc && validatePersonalCode(pc) && !maxAgeFromPersonalCode(pc, 18);
      if (isUnder18) {
        setShowUnderageDialog(true);
        return;
      }
      onInvalid?.(form.formState.errors);
    },
  );

  const handleSwitchToChild = () => {
    const {
      payerFirstName,
      payerLastName,
      payerPersonalCode,
      payerPhone,
      payerEmail,
    } = form.getValues();
    form.setValue('patientFirstName', payerFirstName || '', {
      shouldDirty: true,
    });
    form.setValue('patientLastName', payerLastName || '', {
      shouldDirty: true,
    });
    form.setValue('patientPersonalCode', payerPersonalCode || '', {
      shouldDirty: true,
    });
    form.setValue('payerFirstName', '', { shouldDirty: true });
    form.setValue('payerLastName', '', { shouldDirty: true });
    form.setValue('payerPersonalCode', '', { shouldDirty: true });
    form.setValue('payerPhone', '', { shouldDirty: true });
    form.setValue('payerEmail', '', { shouldDirty: true });
    form.setValue('registerChild', true, { shouldDirty: true });
    setShowUnderageDialog(false);
  };

  // ---- Redirect to pediatrics (isAdultsOnly case) ----
  const history = useHistory();
  const vgskServiceTypeId = process.env.REACT_APP_VGSK_SERVICE_TYPE_EXT_ID;
  const { get: getServiceTypeId, response: getServiceTypeIdResponse } =
    useFetch('/Services/ServiceTypeId');

  const handleRedirectToPediatrics = async () => {
    try {
      if (!vgskServiceTypeId) return;
      const response = await getServiceTypeId(
        `?serviceTypeExternalId=${vgskServiceTypeId}`,
      );
      if (!getServiceTypeIdResponse.ok || !response) return;

      history.push(`/services/${response.categoryId}/${response.id}`);
      setShowUnderageDialog(false);
    } catch (e) {
      console.error('Redirect to pediatrics failed', e);
    }
  };

  const onPersonSelect = useMemo(
    () =>
      function onContactSelect(item) {
        const p = item?.person;
        if (!p) return;
        form.setValue('patientFirstName', p.name || '', {
          shouldDirty: true,
          shouldValidate: false,
        });
        form.setValue('patientLastName', p.surname || '', {
          shouldDirty: true,
          shouldValidate: false,
        });
        form.setValue('patientPersonalCode', p.personalCode || '', {
          shouldDirty: true,
          shouldValidate: false,
        });
        setLocalChildSelected(true);
        setLocalSelectedChildEspbiEsi(p.espbiEsi || '');
      },
    [form],
  );

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Heading variant="6">Paciento informacija</Heading>

        {!service?.isAdultsOnly && (
          <div className="flex items-center gap-2">
            <Switch
              id="registerChild"
              checked={service?.isChildrenOnly ? true : registerChild}
              disabled={service?.isChildrenOnly}
              onCheckedChange={(checked) => {
                if (service?.isChildrenOnly) return;
                if (!checked) {
                  [
                    'patientFirstName',
                    'patientLastName',
                    'patientPersonalCode',
                  ].forEach((key) =>
                    form.setValue(key, '', { shouldDirty: true }),
                  );
                  setLocalChildSelected(false);
                  setLocalSelectedChildEspbiEsi('');
                }
                form.setValue('registerChild', checked);
              }}
            />
            <label
              htmlFor="registerChild"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              {service?.isChildrenAgeRestrictionApplicable &&
              service.childAgeFrom
                ? t('registration.form.fieldChildAgeFromToCheckbox', {
                    ageFrom: service.childAgeFrom,
                    ageTo: service.childAgeTo ?? 18,
                  })
                : t('registration.form.fieldChildCheckbox')}
            </label>
          </div>
        )}
      </div>

      <div className="space-y-6 mt-5">
        <Form {...form}>
          <form
            id="patient-form-id"
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();

              const pc = form.getValues('payerPersonalCode');
              const isUnder18 =
                pc &&
                validatePersonalCode(pc) &&
                !maxAgeFromPersonalCode(pc, 18);

              // 🧠 Check before validation
              if (isUnder18) {
                setShowUnderageDialog(true);
                return; // ❌ stop here, don’t run validation or submission
              }

              // ✅ otherwise run full validation + submission
              handleSubmit(e);
            }}
          >
            {registerChild && (
              <div className="space-y-4">
                <FormField
                  name="patientFirstName"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vardas</FormLabel>
                      <FormControl>
                        {hasSuggestions ? (
                          <InputDropdown
                            {...field}
                            placeholder="Pasirinkite arba įveskite vardą"
                            onBlur={trimOnBlur(field)}
                            items={peopleItems}
                            renderItem={(item) => (
                              <div className="flex flex-col">
                                <span>
                                  {item.person.name} {item.person.surname}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {item.person.personalCode}
                                </span>
                              </div>
                            )}
                            onItemSelect={onPersonSelect}
                          />
                        ) : (
                          <Input {...field} onBlur={trimOnBlur(field)} />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="patientLastName"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pavardė</FormLabel>
                      <FormControl>
                        <Input {...field} onBlur={trimOnBlur(field)} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="patientPersonalCode"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asmens kodas</FormLabel>
                      <FormControl>
                        <Input {...field} onBlur={trimOnBlur(field)} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {registerChild && <Heading variant="6">Mokėtojo duomenys</Heading>}

            <div className="space-y-4">
              <FormField
                name="payerFirstName"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vardas</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLocked('payerFirstName')}
                        {...field}
                        onBlur={trimOnBlur(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="payerLastName"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pavardė</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLocked('payerLastName')}
                        {...field}
                        onBlur={trimOnBlur(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="payerPhone"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefono numeris</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        disabled={isLocked('payerPhone')}
                        {...field}
                        onBlur={trimOnBlur(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="payerEmail"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>El. pašto adresas</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        disabled={isLocked('payerEmail')}
                        {...field}
                        onBlur={trimOnBlur(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="payerPersonalCode"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Asmens kodas</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLocked('payerPersonalCode')}
                        {...field}
                        onBlur={trimOnBlur(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      </div>

      <UnderageDialog
        open={showUnderageDialog}
        onOpenChange={setShowUnderageDialog}
        isAdultsOnly={service?.isAdultsOnly}
        onCancel={() => setShowUnderageDialog(false)}
        onSwitchToChild={handleSwitchToChild}
        onRedirectToPediatrics={handleRedirectToPediatrics}
      />

      <ChildAgeRestrictionDialog
        open={showChildAgeRestrictionModal}
        onOpenChange={setShowChildAgeRestrictionModal}
        isChildrenAgeRestrictionApplicable={Boolean(
          service?.isChildrenAgeRestrictionApplicable,
        )}
        childAgeFrom={service?.childAgeFrom}
        childAgeTo={service?.childAgeTo}
        onClose={() => setShowChildAgeRestrictionModal(false)}
      />
    </div>
  );
};

export default PatientRegistrationForm;
